import { isObject } from 'lodash';
import { COLORS, EARLY, EMERGENT, DEVELOPING, CONSOLIDATING, EXTENDING } from '../constants';
import {
  WORDS,
  SENTENCES,
  WORDS_AND_SENTENCES,
  TEXT_PRESENTATION,
  TEXT_STRUCTURE,
  LANGUAGE_FEATURES,
  WRITING_PROFORMA,
} from '../constants/navigation';
import {
  EmergentContentSection,
  ExemplarWord,
  LaterExemplar,
  WordsPageTitle,
  WordWithLanguageFeatures,
  WordWithTextPresentation,
} from '../types';

type StageColors = {
  main: string;
  gradient: string;
  dark: string;
  light: string;
  label: string;
};

export const getStageColors = (stage: string): StageColors =>
  ({
    [EARLY]: {
      main: COLORS.cadmiumViolet,
      gradient: COLORS.mainGradientEarly,
      dark: COLORS.americanViolet,
      light: COLORS.platinum,
      label: COLORS.languidLavender,
    },
    [EMERGENT]: {
      main: COLORS.debianRed,
      gradient: COLORS.mainGradientEmergent,
      dark: COLORS.pinkRaspberry,
      light: COLORS.pinkLace,
      label: COLORS.classicRose,
    },
    [DEVELOPING]: {
      main: COLORS.metallicSeaweed,
      gradient: COLORS.mainGradientDeveloping,
      dark: COLORS.skobeloff,
      light: COLORS.water,
      label: COLORS.water,
    },
    [CONSOLIDATING]: {
      main: COLORS.formBlue,
      gradient: COLORS.mainGradientConsolidating,
      // TODO: Dark color should be provided
      dark: '#00617e',
      light: COLORS.spindle,
      label: COLORS.spindle,
    },
    [EXTENDING]: {
      main: COLORS.philippineGreen,
      gradient: COLORS.mainGradientExtending,
      // TODO: Dark color should be provided
      dark: '#015e2d',
      light: COLORS.caper,
      label: COLORS.caper,
    },
  }[stage] ||
  ({
    main: COLORS.seaBlue,
    gradient: COLORS.mainGradient,
  } as StageColors));

export const getAppropriateExemplarRoutes = (stage: string): string[] => {
  switch (stage) {
    case EMERGENT:
      return [WORDS, SENTENCES, WORDS_AND_SENTENCES];
    case EARLY:
    case DEVELOPING:
    case CONSOLIDATING:
    case EXTENDING:
      return [TEXT_PRESENTATION, TEXT_STRUCTURE, LANGUAGE_FEATURES, WRITING_PROFORMA];
    default:
      return [];
  }
};

const addWordToLine = (line: WordWithLanguageFeatures[], word: ExemplarWord): void => {
  line.push({
    type: 'word',
    word: word,
    ...(word.language && { language: word.language }),
    ...(word.phrase && { isPhrase: true }),
    ...(word.prepositional_phrase && { isPrepositionalPhrase: true }),
  });
};

export const getLineWithLanguageFeatures = (words: ExemplarWord[]): WordWithLanguageFeatures[] => {
  const line: WordWithLanguageFeatures[] = [];

  // eslint-disable-next-line complexity
  words.forEach((word, index) => {
    addWordToLine(line, word);

    // It's not the last word, and the next word doesn't have 'nospacing' style
    if (index !== words.length - 1 && !words[index + 1].style?.includes('nospacing')) {
      const nextWord = words[index + 1];
      line.push({
        type: 'space',
        ...(nextWord.language === word.language &&
          !word.style?.includes('separate-next') && { language: word.language }),
        ...(word.phrase && nextWord.phrase && !word.style?.includes('separate-next-phrase') && { isPhrase: true }),
        ...(word.prepositional_phrase &&
          nextWord.prepositional_phrase &&
          !word.style?.includes('separate-next-phrase') && { isPrepositionalPhrase: true }),
      });
    }
  });

  return line;
};

export const getLineWithTextPresentation = (words: ExemplarWord[]): WordWithTextPresentation[] => {
  const line: WordWithTextPresentation[] = [];

  words.forEach((word, index) => {
    line.push({
      type: 'word',
      word: word,
      ...(word.text_type && { text_type: word.text_type }),
    });

    // It's not the last word, and the next word doesn't have 'nospacing' style
    if (index !== words.length - 1 && !words[index + 1].style?.includes('nospacing')) {
      const nextWord = words[index + 1];

      line.push({
        type: 'space',
        ...(nextWord.text_type === word.text_type && { text_type: word.text_type }),
      });
    }
  });

  return line;
};

export const getTextAlignForSection = (styles?: string[]): string | undefined => {
  if (Array.isArray(styles)) {
    return styles.find(style => style.indexOf('-aligned') !== -1)?.replace('-aligned', '');
  }
};

export const getSectionColor = (section: EmergentContentSection | WordsPageTitle | string): string | undefined => {
  return isObject(section) && 'color' in section ? `#${section?.color}` : undefined;
};

export const getHybridLabel = (exemplar: LaterExemplar): string => {
  const additionalLabels = exemplar.additional_text_types as string[];
  const label = additionalLabels.slice(1, 2)[0];

  return `${label} Text Type`;
};
